import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, TablePagination, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, CircularProgress, Button, InputAdornment, Autocomplete,
  IconButton, Menu, Divider, Tooltip, useMediaQuery, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckIcon from '@mui/icons-material/Check';
import { getAppointments, getDoctors, getPatients, updateAppointment, exportAppointments, getAppointmentTypes } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG, APPOINTMENT_STATE_OPTIONS } from '../constants/appointmentStates';
import AppointmentDialog from '../components/AppointmentDialog';
import AppointmentTypeChip from '../components/AppointmentTypeChip';
import { useNewAppointmentFlow } from '../components/NewAppointmentFlow';
import AddIcon from '@mui/icons-material/Add';

// ── Helpers ────────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().substring(0, 10);
const daysFromNow = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().substring(0, 10);
};
const fmtDate = (d) => d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtTime = (d) => d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });

const isToday = (dt) => {
  const t = new Date(); return dt.getFullYear() === t.getFullYear() && dt.getMonth() === t.getMonth() && dt.getDate() === t.getDate();
};
const isTomorrow = (dt) => {
  const t = new Date(); t.setDate(t.getDate() + 1);
  return dt.getFullYear() === t.getFullYear() && dt.getMonth() === t.getMonth() && dt.getDate() === t.getDate();
};
const dateKey = (dt) => `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;

// Capitalize first char
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const STATE_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  ...APPOINTMENT_STATE_OPTIONS.map(({ value, label }) => ({ value, label })),
];

// Grilla compartida entre el header y cada fila — con flexbox, cada fila es
// un contenedor de layout aislado y su ancho de columna podía divergir
// ligeramente según el contenido de esa fila puntual (texto más largo,
// wrapping distinto), desalineando el header respecto a los datos. Con
// CSS Grid y la MISMA plantilla de columnas en todos los elementos, el
// ancho de cada columna es literalmente el mismo número de píxeles en
// todas las filas, sin importar su contenido.
// minmax(0, Nfr) en vez de Nfr a secas: un track `1fr` de CSS Grid no tiene
// límite mínimo implícito, así que un texto sin wrap (la columna Notas, por
// ejemplo) podía empujar la columna a crecer y desalinear/solapar el resto
// en vez de truncarse — minmax(0, ...) fuerza a la columna a respetar su
// ancho asignado y dejar que noWrap+ellipsis del contenido hagan su trabajo.
// Columna Tipo (90px, sm+) — antes el tipo de turno iba como texto chico
// debajo de la hora, mezclado con la duración, y se perdía visualmente
// ("no se encuentra" — reportado 2026-11-02). Ahora es su propia columna,
// igual criterio que Médico (oculta en xs, aparece desde sm).
const ROW_GRID_TEMPLATE = {
  xs: '48px minmax(0, 1fr) 124px 40px',
  sm: '48px minmax(0, 1.6fr) minmax(0, 1fr) 90px 124px 40px',
  md: '48px minmax(0, 1.6fr) minmax(0, 1fr) 90px 124px minmax(0, 1fr) 40px',
};

// Transitions allowed per state (what can you change it TO)
const TRANSITIONS = {
  pendiente:   ['confirmado', 'atendido', 'cancelado', 'ausente'],
  confirmado:  ['atendido', 'cancelado', 'ausente', 'pendiente'],
  // en_consulta: el médico ya llamó al paciente (normalmente vía el botón
  // "Llamar" de la sala de espera) — corrección manual de emergencia
  // desde acá si algo quedó mal, mismo criterio que el resto de estados.
  en_consulta: ['atendido', 'cancelado', 'ausente'],
  ausente:     ['pendiente', 'cancelado'],
  cancelado:   ['pendiente'],
  atendido:    [],
  libre:       [],
};

const TRANSITION_LABELS = {
  confirmado: 'Confirmar',
  atendido:   'Marcar atendido',
  cancelado:  'Cancelar turno',
  ausente:    'Marcar ausente',
  pendiente:  'Volver a pendiente',
};

// ── StatSummary ────────────────────────────────────────────────────────────

const StatSummary = ({ today, week, total, loading }) => (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
    {[
      { label: 'Hoy', value: today, color: 'error.main' },
      { label: 'Esta semana', value: week, color: 'warning.main' },
      { label: 'Total filtrado', value: total, color: 'text.primary' },
    ].map(({ label, value, color }) => (
      <Paper key={label} variant="outlined" sx={{ px: 2, py: 1, minWidth: 110, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="h5" fontWeight={700} color={color}>
          {loading ? '–' : value}
        </Typography>
      </Paper>
    ))}
  </Box>
);

// ── DateGroupHeader ────────────────────────────────────────────────────────

const DateGroupHeader = ({ dt, count }) => {
  const today = isToday(dt), tomorrow = isTomorrow(dt);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 1.5, py: 0.75, mt: 1,
      bgcolor: today ? 'primary.50' : 'grey.50',
      borderLeft: `3px solid ${today ? '#1565c0' : '#bdbdbd'}`,
      borderRadius: '0 4px 4px 0',
    }}>
      <Typography variant="subtitle2" fontWeight={700} color={today ? 'primary.main' : 'text.primary'}>
        {today ? 'HOY — ' : tomorrow ? 'MAÑANA — ' : ''}{cap(fmtDate(dt))}
      </Typography>
      <Chip label={`${count} turno${count !== 1 ? 's' : ''}`} size="small"
        color={today ? 'primary' : 'default'} variant="outlined"
        sx={{ height: 20, fontSize: '0.68rem' }} />
    </Box>
  );
};

// ── AppointmentRow ─────────────────────────────────────────────────────────

const AppointmentRow = ({ ap, doctorMap, canEdit, onStateChange, onEdit, onNavigatePatient }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [saving, setSaving] = useState(false);

  const dt     = new Date(ap.start_time + 'Z');
  const state  = STATE_CONFIG[ap.state] || STATE_CONFIG.pendiente;
  const doc    = doctorMap[ap.doctor_id];
  const isPast = dt < new Date() && !isToday(dt);
  const transitions = canEdit ? (TRANSITIONS[ap.state] || []) : [];

  const handleTransition = async (newState) => {
    setMenuAnchor(null);
    setSaving(true);
    try { await onStateChange(ap.id, newState, ap.state); } finally { setSaving(false); }
  };

  return (
    <Box
      // Antes solo el botón ⋮ (40px) abría el turno — en mobile, con las
      // columnas de Médico/Notas ocultas, la fila quedaba casi sin zona
      // clickeable real, y tocar en cualquier otro lado "no hacía nada".
      // Ahora toda la fila abre el turno (mismo onEdit que ya usaba el
      // menú), salvo los controles con su propio click (nombre del
      // paciente, menú ⋮), que cortan la propagación para no disparar
      // los dos a la vez. Bug real reportado 2026-10-30.
      onClick={() => canEdit && onEdit(ap)}
      sx={{
      display: 'grid',
      gridTemplateColumns: ROW_GRID_TEMPLATE,
      alignItems: 'center', columnGap: 1.5,
      px: 2, py: 1.25,
      borderBottom: '1px solid', borderColor: 'divider',
      bgcolor: saving ? 'action.hover' : 'background.paper',
      opacity: ap.state === 'cancelado' ? 0.6 : 1,
      cursor: canEdit ? 'pointer' : 'default',
      '&:hover': { bgcolor: 'action.hover' },
      transition: 'background-color 0.1s',
    }}>
      {/* Hora */}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} color={isPast ? 'text.disabled' : 'text.primary'}>
          {fmtTime(dt)}
        </Typography>
        <Typography variant="caption" color="text.disabled">{ap.duration_minutes}min</Typography>
      </Box>

      {/* Paciente */}
      <Box sx={{ minWidth: 0 }}>
        {ap.patient ? (
          <Typography
            variant="body2" fontWeight={600}
            sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
            noWrap onClick={(e) => { e.stopPropagation(); onNavigatePatient(ap.patient_id); }}
          >
            {ap.patient.name}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Sin paciente</Typography>
        )}
        {ap.patient?.phone && (
          <Typography variant="caption" color="text.secondary" display="block" noWrap>{ap.patient.phone}</Typography>
        )}
      </Box>

      {/* Médico */}
      <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
        {doc ? (
          <>
            <Typography variant="body2" noWrap>{doc.name}</Typography>
            {doc.specialty && <Typography variant="caption" color="text.secondary" noWrap>{doc.specialty}</Typography>}
          </>
        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
      </Box>

      {/* Tipo de turno — antes abajo de la hora, mezclado con la duración
          ("no se encuentra" — reportado 2026-11-02). Ahora su propia
          columna (ver ROW_GRID_TEMPLATE), acotada con noWrap + Tooltip
          para nombres largos. Ya viene precargado por joinedload en el
          backend, sin fetch extra. */}
      <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
        {ap.appointment_type ? (
          <Tooltip title={ap.appointment_type.name}>
            <span><AppointmentTypeChip appointmentType={ap.appointment_type} /></span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </Box>

      {/* Estado */}
      <Box sx={{ minWidth: 0 }}>
        <Chip
          label={state.label}
          size="small"
          sx={{ bgcolor: state.color, color: 'white', fontWeight: 700, fontSize: '0.7rem' }}
        />
        {ap.cancellation_reason && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }} noWrap>
            {ap.cancellation_reason}
          </Typography>
        )}
      </Box>

      {/* Notas */}
      <Box sx={{ minWidth: 0, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
        {ap.notes && (
          <Tooltip title={ap.notes}>
            <Typography variant="caption" color="text.secondary" noWrap>{ap.notes}</Typography>
          </Tooltip>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
        {saving ? (
          <CircularProgress size={16} sx={{ mx: 1 }} />
        ) : (
          <IconButton size="small" onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { minWidth: 180 } }}
        >
          {ap.patient_id && (
            <MenuItem dense onClick={() => { setMenuAnchor(null); onNavigatePatient(ap.patient_id); }}>
              <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />Ver paciente
            </MenuItem>
          )}
          {canEdit && (
            <MenuItem dense onClick={() => { setMenuAnchor(null); onEdit(ap); }}>
              <EditIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />Editar turno
            </MenuItem>
          )}
          {transitions.length > 0 && <Divider />}
          {transitions.map(t => (
            <MenuItem key={t} dense onClick={() => handleTransition(t)}>
              <CheckIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
              {TRANSITION_LABELS[t]}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const AppointmentSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotification();
  const { doctorId, isMedico, isAdmin, isRecepcionista } = useAuth();
  // Médico tiene el mismo acceso que admin/recepcionista sobre turnos desde
  // 2026-10-09 (ver CalendarGrid.js) — este canEdit se había quedado
  // excluyéndolo sin ninguna razón real, reportado por el usuario
  // 2026-10-19 ("no veo el botón Nuevo turno... tampoco en el perfil de médico").
  const canEdit = isAdmin || isRecepcionista || isMedico;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Llegada desde el atajo "Pendientes" del dashboard de recepción: arranca
  // ya filtrado por turnos pendientes sin confirmar, incluyendo los que
  // quedaron atrás en el tiempo (no solo los de hoy en adelante) — ese es
  // justamente el caso que el conteo del dashboard no cubre.
  const incomingFilter = location.state?.initialStateFilter;

  // Filters
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedState, setSelectedState] = useState(incomingFilter ? incomingFilter.state : '');
  // Pedido explícito del usuario (2026-11-04): "interesante poder filtrar
  // por tipo... ecografías, scan fetal, etc" — útil para ver de un vistazo
  // todos los turnos de un tipo puntual (ej. auditar cuántas ecografías se
  // hicieron en el mes), igual que ya se puede filtrar por médico/estado.
  const [selectedType, setSelectedType] = useState('');
  const [startDate, setStartDate] = useState(incomingFilter ? incomingFilter.startDate : todayStr());
  const [endDate, setEndDate] = useState(daysFromNow(90));
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    if (incomingFilter) navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line
  }, []);

  // Init medico doctor filter
  useEffect(() => {
    if (isMedico && doctorId) setSelectedDoctor(doctorId);
  }, [isMedico, doctorId]);

  // Data
  const [appointments, setAppointments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Lookup data
  const [doctors, setDoctors] = useState([]);
  const [doctorMap, setDoctorMap] = useState({});
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientInput, setPatientInput] = useState('');
  const patientTimer = useRef(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEvent, setDialogEvent] = useState(null);
  const [dialogPatients, setDialogPatients] = useState([]);
  const [confirmCancelInConsultation, setConfirmCancelInConsultation] = useState(null); // { apId, newState }

  useEffect(() => {
    getDoctors({ limit: 500 }).then(r => {
      const items = r.data?.items || [];
      setDoctors(items);
      const map = {}; items.forEach(d => { map[d.id] = d; }); setDoctorMap(map);
    }).catch(() => {});
    getAppointmentTypes().then(r => setAppointmentTypes(r.data || [])).catch(() => {});
  }, []);

  // Debounced patient search
  useEffect(() => {
    clearTimeout(patientTimer.current);
    if (patientInput.length < 2) { setPatientOptions([]); return; }
    patientTimer.current = setTimeout(async () => {
      try {
        const res = await getPatients({ search: patientInput, limit: 20 });
        setPatientOptions(res.data?.items || []);
      } catch { setPatientOptions([]); }
    }, 300);
  }, [patientInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sort: sortAsc ? 'asc' : 'desc',
        ...(selectedDoctor ? { doctor_id: selectedDoctor } : {}),
        ...(selectedPatient ? { patient_id: selectedPatient.id } : {}),
        ...(selectedState ? { state: selectedState } : {}),
        ...(selectedType ? { appointment_type_id: selectedType } : {}),
        ...(startDate ? { start_date: new Date(startDate).toISOString() } : {}),
        ...(endDate ? { end_date: new Date(endDate + 'T23:59:59').toISOString() } : {}),
      };
      const res = await getAppointments(params);
      setAppointments(res.data?.items || []);
      setTotal(res.data?.total ?? 0);
    } catch { setAppointments([]); setTotal(0); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, sortAsc, selectedDoctor, selectedPatient, selectedState, selectedType, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const {
    canCreateAppointment, showForm: showNewApptForm, openForm: openNewApptForm,
    closeForm: closeNewApptForm, handleContinue: handleContinueNewAppt,
    canContinue: canContinueNewAppt, FormFields: NewAppointmentFormFields,
    AppointmentDialogNode: newApptDialogNode,
  } = useNewAppointmentFlow({ doctorsArr: doctors, doctorsMap: doctorMap, onSaved: load });

  // Summary stats from current result set
  const todayCount = appointments.filter(a => isToday(new Date(a.start_time + 'Z'))).length;
  const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
  const weekCount = appointments.filter(a => { const d = new Date(a.start_time + 'Z'); return d >= new Date() && d <= weekEnd; }).length;

  const clearFilters = () => {
    if (!isMedico) setSelectedDoctor('');
    setSelectedPatient(null);
    setSpecialtyFilter('');
    setPatientInput('');
    setSelectedState('');
    setSelectedType('');
    setStartDate(todayStr());
    setEndDate(daysFromNow(90));
    setSortAsc(true);
    setPage(0);
  };

  const applyStateChange = async (apId, newState) => {
    try {
      await updateAppointment(apId, { state: newState });
      setAppointments(prev => prev.map(a => a.id === apId ? { ...a, state: newState } : a));
    } catch (err) {
      notify(err.userMessage || 'No se pudo actualizar el estado', 'error');
    }
  };

  // El médico está atendiendo a este paciente ahora mismo — cancelarlo o
  // marcarlo ausente desde acá corta una consulta en curso sin que nadie
  // se entere. No lo bloqueamos (puede ser una corrección legítima), pero
  // confirmamos antes. Pedido explícito del usuario (2026-11-04).
  const handleStateChange = async (apId, newState, currentState) => {
    if (currentState === 'en_consulta' && (newState === 'cancelado' || newState === 'ausente')) {
      setConfirmCancelInConsultation({ apId, newState });
      return;
    }
    await applyStateChange(apId, newState);
  };

  const handleEdit = (ap) => {
    setDialogEvent(ap);
    setDialogOpen(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        ...(selectedDoctor ? { doctor_id: selectedDoctor } : {}),
        ...(selectedPatient ? { patient_id: selectedPatient.id } : {}),
        ...(selectedState ? { state: selectedState } : {}),
        ...(startDate ? { start_date: new Date(startDate).toISOString() } : {}),
        ...(endDate ? { end_date: new Date(endDate + 'T23:59:59').toISOString() } : {}),
      };
      const res = await exportAppointments(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Turnos_${todayStr()}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { notify('No se pudo exportar', 'error'); }
    finally { setExporting(false); }
  };

  const filteredDoctors = specialtyFilter
    ? doctors.filter(d => d.specialty?.toLowerCase().includes(specialtyFilter.toLowerCase()))
    : doctors;

  const hasFilters = (!isMedico && selectedDoctor) || selectedPatient || selectedState || selectedType || specialtyFilter
    || startDate !== todayStr() || endDate !== daysFromNow(90);

  // Group appointments by date for display
  const groupedRows = [];
  let lastKey = null;
  let currentGroup = null;
  appointments.forEach(ap => {
    const dt = new Date(ap.start_time + 'Z');
    const key = dateKey(dt);
    if (key !== lastKey) {
      currentGroup = { key, dt, items: [] };
      groupedRows.push(currentGroup);
      lastKey = key;
    }
    currentGroup.items.push(ap);
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Listado de turnos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {isMedico ? 'Mis turnos próximos' : 'Todos los turnos — filtrá y gestioná desde aquí'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canCreateAppointment && (
            isMobile ? (
              <Tooltip title="Nuevo turno">
                <IconButton color="primary" onClick={openNewApptForm} sx={{ border: 1, borderColor: 'primary.main' }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNewApptForm}>
                Nuevo turno
              </Button>
            )
          )}
          {isMobile ? (
            <Tooltip title="Exportar Excel">
              <span>
                <IconButton onClick={handleExport} disabled={exporting || total === 0} sx={{ border: 1, borderColor: 'divider' }}>
                  {exporting ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant="outlined" size="small" startIcon={<DownloadIcon />}
              onClick={handleExport} disabled={exporting || total === 0}
            >
              {exporting ? 'Exportando…' : 'Exportar Excel'}
            </Button>
          )}
        </Box>
      </Box>

      {showNewApptForm && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
          <NewAppointmentFormFields />
          <Button size="small" onClick={closeNewApptForm}>Cancelar</Button>
          <Button size="small" variant="contained" disabled={!canContinueNewAppt} onClick={handleContinueNewAppt}>
            Continuar
          </Button>
        </Paper>
      )}

      {/* Summary */}
      <StatSummary today={todayCount} week={weekCount} total={total} loading={loading} />

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterListIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
          <Typography variant="subtitle2">Filtros</Typography>
          {hasFilters && (
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: 'auto' }}>
              Restablecer
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: { sm: 'wrap' }, gap: 1.5 }}>
          {/* Paciente */}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '2 1 240px' }, minWidth: { sm: 200 } }}>
            <Autocomplete
              options={patientOptions}
              getOptionLabel={p => `${p.name}${p.phone ? ` (${p.phone})` : ''}`}
              value={selectedPatient}
              inputValue={patientInput}
              onInputChange={(_, v) => setPatientInput(v)}
              onChange={(_, v) => { setSelectedPatient(v); setPage(0); }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText={patientInput.length < 2 ? 'Escribí 2+ letras' : 'Sin resultados'}
              renderInput={params => (
                <TextField {...params} label="Paciente" size="small"
                  InputProps={{ ...params.InputProps, startAdornment: (
                    <><InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>{params.InputProps.startAdornment}</>
                  )}}
                />
              )}
            />
          </Box>

          {/* Especialidad (solo admin/recep) */}
          {!isMedico && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: { sm: 140 } }}>
              <TextField fullWidth size="small" label="Especialidad"
                value={specialtyFilter}
                onChange={e => { setSpecialtyFilter(e.target.value); setSelectedDoctor(''); setPage(0); }}
              />
            </Box>
          )}

          {/* Médico */}
          {!isMedico && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '2 1 210px' }, minWidth: { sm: 180 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Médico</InputLabel>
                <Select value={selectedDoctor} label="Médico"
                  onChange={e => { setSelectedDoctor(e.target.value); setPage(0); }}>
                  <MenuItem value=""><em>Todos los médicos</em></MenuItem>
                  {filteredDoctors.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}{d.specialty ? ` — ${d.specialty}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Estado */}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: { sm: 150 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={selectedState} label="Estado"
                onChange={e => { setSelectedState(e.target.value); setPage(0); }}>
                {STATE_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.value ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATE_CONFIG[o.value]?.color }} />
                        {o.label}
                      </Box>
                    ) : o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tipo de turno — pedido explícito del usuario (2026-11-04):
              "interesante poder filtrar... ecografías, scan fetal, etc".
              Solo se muestra si la clínica tiene algún tipo cargado — no
              tiene sentido un filtro vacío. */}
          {appointmentTypes.length > 0 && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' }, minWidth: { sm: 150 } }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de turno</InputLabel>
                <Select value={selectedType} label="Tipo de turno"
                  onChange={e => { setSelectedType(e.target.value); setPage(0); }}>
                  <MenuItem value=""><em>Todos los tipos</em></MenuItem>
                  {appointmentTypes.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color }} />
                        {t.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Fechas — las dos siempre lado a lado, incluso en mobile: son
              campos cortos y separarlas en filas propias desperdicia
              espacio vertical sin necesidad. */}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 140px' }, minWidth: { sm: 130 }, display: 'flex', gap: 1.5 }}>
            <TextField fullWidth size="small" type="date" label="Desde"
              value={startDate} onChange={e => { setStartDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
            <TextField fullWidth size="small" type="date" label="Hasta"
              value={endDate} onChange={e => { setEndDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Box>

          {/* Sort toggle — en mobile colapsa a solo ícono, el texto largo
              ("Próximos primero"/"Recientes primero") no entraba junto al
              resto de los filtros apilados a ancho completo. */}
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'flex-end' }, flexShrink: 0, flex: { xs: '1 1 100%', sm: 'initial' } }}>
            <Tooltip title={sortAsc ? 'Más próximos primero' : 'Más recientes primero'}>
              {isMobile ? (
                <Button
                  size="small" variant="outlined" fullWidth
                  startIcon={sortAsc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  onClick={() => { setSortAsc(p => !p); setPage(0); }}
                >
                  {sortAsc ? 'Próximos primero' : 'Recientes primero'}
                </Button>
              ) : (
                <Button
                  size="small" variant="outlined"
                  startIcon={sortAsc ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  onClick={() => { setSortAsc(p => !p); setPage(0); }}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  {sortAsc ? 'Próximos primero' : 'Recientes primero'}
                </Button>
              )}
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Lista agrupada por fecha */}
      <Paper>
        {/* Column headers — misma gridTemplateColumns que AppointmentRow, para
            que las columnas queden pixel-a-pixel alineadas con los datos. */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: ROW_GRID_TEMPLATE, columnGap: 1.5,
          px: 2, py: 0.75,
          bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider',
          borderRadius: '4px 4px 0 0',
        }}>
          <Typography variant="caption" color="text.secondary">Hora</Typography>
          <Typography variant="caption" color="text.secondary">Paciente</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Médico</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Tipo</Typography>
          <Typography variant="caption" color="text.secondary">Estado</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>Notas</Typography>
          <Box />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : appointments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <EventBusyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No hay turnos con esos filtros</Typography>
            {hasFilters && (
              <Button sx={{ mt: 1.5 }} size="small" onClick={clearFilters}>Restablecer filtros</Button>
            )}
          </Box>
        ) : (
          groupedRows.map(group => (
            <Box key={group.key}>
              <DateGroupHeader dt={group.dt} count={group.items.length} />
              {group.items.map(ap => (
                <AppointmentRow
                  key={ap.id}
                  ap={ap}
                  doctorMap={doctorMap}
                  canEdit={canEdit}
                  onStateChange={handleStateChange}
                  onEdit={handleEdit}
                  onNavigatePatient={(pid) => navigate(`/patients/${pid}`)}
                />
              ))}
            </Box>
          ))
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Filas:"
          labelDisplayedRows={({ from, to, count }) =>
            count === 0 ? 'Sin resultados' : `${from}–${to} de ${count}`
          }
        />
      </Paper>

      {/* AppointmentDialog para edición completa */}
      {dialogOpen && (
        <AppointmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          isEdit
          selectedEvent={dialogEvent}
          doctorId={dialogEvent?.doctor_id}
          doctor={doctors.find(d => d.id === dialogEvent?.doctor_id)}
          patients={dialogPatients}
          setPatients={setDialogPatients}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
      {newApptDialogNode}

      <Dialog open={!!confirmCancelInConsultation} onClose={() => setConfirmCancelInConsultation(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Este turno está en curso</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            El médico está atendiendo a este paciente ahora mismo. ¿Confirmás {confirmCancelInConsultation?.newState === 'cancelado' ? 'cancelar' : 'marcar ausente'} igual?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancelInConsultation(null)}>Volver</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              const { apId, newState } = confirmCancelInConsultation;
              setConfirmCancelInConsultation(null);
              applyStateChange(apId, newState);
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentSearch;
